import {
  Form, Input, FormInstance, FormRule,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { AppConstants } from 'constants/index';
import { StyledFormMainWrapper } from 'components/styledComponents/form/Form.styled';
import { i18n } from 'services';
import { FormInputWrapper } from 'components/styledComponents/input/Input.styled';
import { FormItemMarginTopWrapper } from 'shared/Common.styled';
import { FormItemErrorMessage } from 'shared/formControl';

interface ChangePasswordFormProps {
    form: FormInstance;
    translatable?: boolean;
}

const defaultTranslation = i18n.getFixedT('en');
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const ChangePasswordForm = ({
  form: { getFieldValue },
  form,
  translatable = true,
}: ChangePasswordFormProps) => {
  const translation = useTranslation();

  const t = useMemo(() => (translatable ? translation.t : defaultTranslation), [translatable, translation.t]);

  const inputs = {
    oldPassword: 'oldPassword',
    newPassword: 'newPassword',
    confirmPassword: 'confirmPassword',
  };

  const compareToFirstPassword = (rule: FormRule, value: string) => {
    if (value && value !== getFieldValue(inputs.newPassword)) {
      return Promise.reject(
        <FormItemErrorMessage
          errorMassage={(tran: TranslateFn) => {
            const translate = translatable ? tran : defaultTranslation;
            return translate('validate:passwordDontMatch');
          }}
        />,
      );
    }
    return Promise.resolve();
  };

  return (
    <StyledFormMainWrapper layout="vertical" form={form}>
      <FormInputWrapper>
        <Form.Item
          label={t('oldPassword')}
          name={inputs.oldPassword}
          rules={[
            {
              required: true,
              message: (
                <FormItemErrorMessage
                  errorMassage={(tran: TranslateFn) => {
                    const translate = translatable ? tran : defaultTranslation;
                    return translate('validate:requiredField', { fieldName: translate('oldPassword') });
                  }}
                />
              ),
            },
            {
              pattern: new RegExp(AppConstants.regex.password),
              message: (
                <FormItemErrorMessage
                  errorMassage={(tran: TranslateFn) => {
                    const translate = translatable ? tran : defaultTranslation;
                    return translate('validate:password8to40NUL');
                  }}
                />
              ),
            },
          ]}
        >
          <Input.Password type="password" placeholder={t('oldPassword')} />
        </Form.Item>
      </FormInputWrapper>
      <FormItemMarginTopWrapper>
        <FormInputWrapper>
          <Form.Item
            label={t('newPassword')}
            name={inputs.newPassword}
            rules={[
              {
                required: true,
                message: (
                  <FormItemErrorMessage
                    errorMassage={(tran: TranslateFn) => {
                      const translate = translatable ? tran : defaultTranslation;
                      return translate('validate:requiredField', { fieldName: translate('newPassword') });
                    }}
                  />
                ),
              },
              {
                pattern: new RegExp(AppConstants.regex.password),
                message: (
                  <FormItemErrorMessage
                    errorMassage={(tran: TranslateFn) => {
                      const translate = translatable ? tran : defaultTranslation;
                      return translate('validate:password8to40NUL');
                    }}
                  />
                ),
              },
            ]}
          >
            <Input.Password type="password" placeholder={t('newPassword')} />
          </Form.Item>
        </FormInputWrapper>
      </FormItemMarginTopWrapper>
      <FormItemMarginTopWrapper>
        <FormInputWrapper>
          <Form.Item
            label={t('confirmPassword')}
            name={inputs.confirmPassword}
            dependencies={[inputs.newPassword]}
            rules={[
              {
                required: true,
                message: (
                  <FormItemErrorMessage
                    errorMassage={(tran: TranslateFn) => {
                      const translate = translatable ? tran : defaultTranslation;
                      return translate('validate:requiredField', { fieldName: translate('confirmPassword') });
                    }}
                  />
                ),
              },
              {
                validator: compareToFirstPassword,
              },
            ]}
          >
            <Input.Password type="password" placeholder={t('confirmPassword')} />
          </Form.Item>
        </FormInputWrapper>
      </FormItemMarginTopWrapper>
    </StyledFormMainWrapper>
  );
};

export default ChangePasswordForm;
